import type { Point } from '@os/engine';
import { generateKeyBetween } from 'fractional-indexing';
import * as Y from 'yjs';
import type { NewObject } from './commands';
import { newId } from './ids';
import { taskListHeight, type ObjectData, type TaskItem } from './schema';

export type TaskList = Extract<ObjectData, { type: 'tasklist' }>;
export const TASK_DEFAULT_W = 280;

export function taskProgress(items: readonly TaskItem[]): { done: number; total: number } {
  return { done: items.filter((i) => i.done).length, total: items.length };
}

/** A new list centred on `at`: an empty title and one empty task unless given. */
export function newTaskList(at: Point, opts: { title?: string; items?: string[] } = {}): NewObject {
  const texts = opts.items ?? [''];
  const h = taskListHeight(texts.length, false);
  return {
    type: 'tasklist', parentId: null, x: at.x - TASK_DEFAULT_W / 2, y: at.y - h / 2, w: TASK_DEFAULT_W, h, rotation: 0, locked: false, style: {},
    title: opts.title ?? '', compact: false,
    items: texts.map((text) => ({ id: newId(), text, done: false, indent: 0, assignee: null })),
  };
}

const validKey = (k: unknown): string | null => {
  if (typeof k !== 'string') return null;
  try {
    generateKeyBetween(k, null);
    return k;
  } catch {
    return null;
  }
};

/**
 * An order key strictly between two neighbours. A forged key counts as absent, and equal or inverted neighbours
 * (two peers inserted at the same place) give a key after the lower one. Never throws.
 */
export function posBetween(lo: string | null, hi: string | null): string {
  const a = validKey(lo);
  let b = validKey(hi);
  if (a !== null && b !== null && a >= b) b = null;
  return generateKeyBetween(a, b);
}

/** A task's order key as read: anything but a string sorts first. */
const posOf = (m: Y.Map<unknown>): string => {
  const p = m.get('pos');
  return typeof p === 'string' ? p : '';
};

/** The list's tasks in display order, `(pos, id)`; entries that are not maps are skipped. */
export function sortedTaskEntries(items: Y.Map<unknown> | Y.Map<Y.Map<unknown>>): [string, Y.Map<unknown>][] {
  const out: [string, Y.Map<unknown>][] = [];
  items.forEach((v, k) => {
    if (v instanceof Y.Map) out.push([k, v]);
  });
  return out.sort(([ka, a], [kb, b]) => {
    const pa = posOf(a);
    const pb = posOf(b);
    return pa < pb ? -1 : pa > pb ? 1 : ka < kb ? -1 : ka > kb ? 1 : 0;
  });
}

/**
 * Gives a fresh key to every task whose key is invalid or not above the one before it (two peers that added at the same
 * place wrote the same key; nothing could go between them otherwise). Display order is kept; two peers repairing the
 * same state write the same keys. Writes only what it fixes; returns whether it wrote.
 */
export function repairTaskOrder(entries: readonly [string, Y.Map<unknown>][]): boolean {
  let prev: string | null = null;
  let wrote = false;
  entries.forEach(([, m], i) => {
    const k = validKey(m.get('pos'));
    if (k !== null && (prev === null || k > prev)) {
      prev = k;
      return;
    }
    let hi: string | null = null;
    for (let j = i + 1; j < entries.length && hi === null; j++) {
      const n = validKey(entries[j]![1].get('pos'));
      if (n !== null && (prev === null || n > prev)) hi = n;
    }
    prev = posBetween(prev, hi);
    m.set('pos', prev);
    wrote = true;
  });
  return wrote;
}

/** The key that puts a task at `index` of `entries` (already sorted, without that task). */
export function posAt(entries: readonly [string, Y.Map<unknown>][], index: number): string {
  const i = Math.min(Math.max(0, Math.floor(index)), entries.length);
  let lo: string | null = null;
  for (let j = i - 1; j >= 0 && lo === null; j--) lo = validKey(entries[j]![1].get('pos'));
  let hi: string | null = null;
  for (let j = i; j < entries.length && hi === null; j++) {
    const k = validKey(entries[j]![1].get('pos'));
    if (k !== null && (lo === null || k > lo)) hi = k;
  }
  return posBetween(lo, hi);
}
